import { Injectable } from '@angular/core';
import {
  AccountsUserFormPayload,
  AccountUser,
  IAccountsInformationData,
  ICustomerService,
} from '../model/sm-accounts';
import { BehaviorSubject, filter, Observable, of, switchMap, tap } from 'rxjs';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import { BaseService } from 'src/app/core/services/base.httpService';
import {
  AttachmentDownloadRequestType,
  BulkDeleteItemNames,
  ModalPopupMessages,
  popupWidth,
  StatusCodes,
} from 'src/app/shared/utility/constants';
import { MessagePopupComponent } from 'src/app/shared/components/message-popup/message-popup.component';
import { ModalService } from 'src/app/shared/services/modal.service';
import { handleErrorMessage } from '../../shared/operators/error.operator';
import { SnakbarService } from '../../shared/services/snakbar.service';
import { buttonsForSendCredentials } from '../components/accounts/accounts.config';

@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  public ischeckAccordionTabsInfoValid = new BehaviorSubject<boolean>(false);
  public isCheckForAddressInfoData = new BehaviorSubject<boolean>(false);
  public isCheckForGovernmentInfoData = new BehaviorSubject<boolean>(false);
  // Should be valid by default - one the tab is open we can update the validity status for this
  public isCheckForAccountUserData = new BehaviorSubject<boolean>(true);
  public accountUserDataUpdated = new BehaviorSubject<boolean>(true);
  public isCheckForCustomerManagementData = new BehaviorSubject<boolean>(false);
  public requiredAccountInfoData = new BehaviorSubject<any>({});
  public isFromPageSummary = new BehaviorSubject<string>('');
  public isCheckForGovernmentInfoDataValid = new BehaviorSubject<boolean>(
    false
  );
  public isCheckForCustomerManagementDataValid = new BehaviorSubject<boolean>(
    false
  );
  public isCheckForBusinessInfoData = new BehaviorSubject<boolean>(false);
  public isCheckForBusinessInfoDataValid = new BehaviorSubject<boolean>(false);
  public isCreatedLinkedAccountData = new BehaviorSubject<any>('');
  public isDeletedCustomerServicesData = new BehaviorSubject<
    ICustomerService[]
  >([]);
  public isAccountPrevSummary = new BehaviorSubject<any>('');
  public isOrganizationAPICall = new BehaviorSubject<any>({
    status: false,
    data: null,
  });
  public isFetchAccountApi = new BehaviorSubject<boolean>(false);
  public errorHandler = handleErrorMessage();
 
  private readonly routingCriteriaData = new BehaviorSubject<any[]>([]);
  public isRoutingCriteriaData = this.routingCriteriaData.asObservable();
  private readonly routingCriteriaFromAPI = new BehaviorSubject<any[]>([]);
  public isRoutingCriteriaFromAPI = this.routingCriteriaFromAPI.asObservable();
  private addressInfoFormsAvailable = false;

  constructor(
    private readonly baseService: BaseService,
    private readonly modalService: ModalService,
    private readonly snakbarService: SnakbarService
  ) {}

  public checkAccordionTabsInfoValid(data: boolean) {
    this.ischeckAccordionTabsInfoValid.next(data);
  }

  public setAddressInfoFormsAvailable(available: boolean): void {
    this.addressInfoFormsAvailable = available;
  }

  public areAddressInfoFormsAvailable(): boolean {
    return this.addressInfoFormsAvailable;
  }

  public checkAddressInfoData(data: boolean) {
    this.isCheckForAddressInfoData.next(data);
  }

  public checkGovernmentInfoData(data: boolean) {
    this.isCheckForGovernmentInfoData.next(data);
  }

  public checkAccountUserData(data: boolean) {
    this.isCheckForAccountUserData.next(data);
  }
  public refreshAccountUsers() {
    this.accountUserDataUpdated.next(false);
    this.accountUserDataUpdated.next(true);
  }

  public checkCustomerManagementData(data: boolean) {
    this.isCheckForCustomerManagementData.next(data);
  }

  public checkGovernmentInfoDataValid(data: boolean) {
    this.isCheckForGovernmentInfoDataValid.next(data);
  }

  public resetAccordionTabValidityFlags(): void {
    this.checkGovernmentInfoDataValid(false);
    this.checkBusinessInfoDataValid(false);
    this.checkCustomerManagementDataValid(false);
    this.checkAccountUserData(true);
    this.setAddressInfoFormsAvailable(false);
  }

  public checkCustomerManagementDataValid(data: boolean) {
    this.isCheckForCustomerManagementDataValid.next(data);
  }

  public checkBusinessInfoData(data: boolean) {
    this.isCheckForBusinessInfoData.next(data);
  }

  public checkBusinessInfoDataValid(data: boolean) {
    this.isCheckForBusinessInfoDataValid.next(data);
  }

  public setDeletedCustomerServicesData(data: ICustomerService[]) {
    this.isDeletedCustomerServicesData.next(data);
  }

  public checkOrganizationCRUDSuccess(data: any) {
    this.isOrganizationAPICall.next(data);
  }

  public setRequiredDataForAccountInfoPage(data: any): void {
    this.requiredAccountInfoData.next(data);
  }

  public setPageFrom(data: string) {
    this.isFromPageSummary.next(data);
  }

  public setLinkedAccountData(data: any) {
    this.isCreatedLinkedAccountData.next(data);
  }

  public isAccountSummaryPrevSearch(data: string) {
    this.isAccountPrevSummary.next(data);
  }

  public setAccountFetchApi(data: boolean) {
    this.isFetchAccountApi.next(data);
  }

  public setRoutingCriteria(data: any): void {
    const currentData = this.routingCriteriaData.getValue();
    if (data.criteriaId) {
      const existingIndex = currentData.findIndex(
        (item: any) => item.criteriaId === data.criteriaId
      );
      if (existingIndex > -1) {
        currentData[existingIndex] = data;
        this.routingCriteriaData.next([...currentData]);
        return;
      }
    }
    
    if (data.tempId) {
      const existingIndex = currentData.findIndex(
        (item: any) => item.tempId === data.tempId
      );
      if (existingIndex > -1) {
        currentData[existingIndex] = data;
        this.routingCriteriaData.next([...currentData]);
        return;
      }
    }    
    currentData.push(data);
    this.routingCriteriaData.next([...currentData]);
  }

  public getRoutingCriteria(): any[] {
    return this.routingCriteriaData.getValue();
  }

  public clearRoutingCriteria(): void {
    this.routingCriteriaData.next([]);
  }

  public resetRoutingCriteriaState(): void {
    this.clearRoutingCriteria();
    this.clearRoutingCriteriaFromAPI();
  }

  public removeRoutingCriteria(tempId: string): void {
    const currentData = this.routingCriteriaData.getValue();
    const filteredData = currentData.filter((item: any) => item.tempId !== tempId);
    this.routingCriteriaData.next(filteredData);
  }
  public createAccount(data: IAccountsInformationData): Observable<any> {
    return this.baseService
      .initiateApiRequest('PUT', URLConstants.createAccount, data, 'json')
      .pipe(this.errorHandler());
  }

  public updateAccount(data: IAccountsInformationData): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.updateAccount, data, 'json')
      .pipe(this.errorHandler());
  }

  public getSearchData(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.accountSearch, event, '', 'json')
      .pipe(this.errorHandler());
  }

  public checkAccountExistence(accountId: number): Observable<any> {
    const url = URLConstants.checkAccountInfo + accountId;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public autogenerateAccountNumber(accountNumber: string): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.autogenerateAccountNumber,
        accountNumber,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public createFirstAccountNumber(reqBody: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.firstLinkedAccountNumber,
        reqBody,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public deleteAccount(data: any) {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.deleteAccount, data, 'json')
      .pipe(this.errorHandler());
  }

  public addorUpdateCustomerService(data: any) {
    return this.baseService
      .initiateApiRequest(
        'PUT',
        URLConstants.addOrUpdateCustomerGrid,
        data,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public deleteCustomerService(data: any) {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.deleteCustomerGrid, data, 'json')
      .pipe(this.errorHandler());
  }

  // Organisation
  public createOrganization(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.createOrganization,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public updateOrganization(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.updateOrganization,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public fetchOrganization(organizationId: number): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.fetchOrganization + organizationId,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public deleteOrganization(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteOrganization,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public validateOrganization(organizationId: string): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.validateOrganization + organizationId,
        '',
        'json'
      );
  }

  public openAccountDeletePopup(selection: any) {
    const message =
      selection.length > 1
        ? ModalPopupMessages.bulkDeleteMessage(
            selection.length,
            BulkDeleteItemNames.Accounts
          )
        : ModalPopupMessages.singleDeleteMessage(
            AttachmentDownloadRequestType.account
          ) + `${selection[0].accountNumber}?`;
    const dialogRef = this.modalService.openPopup(
      {
        message: message,
        isFooterRequired: true,
        isBulk: selection.length > 1,
        isMultiple: true,
        isForCreateLinkedAccount: false,
      },
      selection.length > 1
        ? ModalPopupMessages.BulkDeleteHeader
        : ModalPopupMessages.singleDeleteHeader(
            AttachmentDownloadRequestType.account
          ),
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    return dialogRef;
  }

  public createOrUpdateUsers(payload: AccountsUserFormPayload) {
    return this.baseService
      .initiateApiRequest(
        'PUT',
        URLConstants.accountUsers.createOrUpdate,
        payload,
        '',
        'json'
      )
      .pipe(
        this.errorHandler({ showRedErrorToast: error => error.status === 400 }),
        tap(response => {
          if (response.statusCode === StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              response.message,
              ModalPopupMessages.success
            );
          }
        })
      );
  }

  public deleteUserConfirmation(selection: AccountUser[]) {
    const message =
      selection.length > 1
        ? ModalPopupMessages.bulkDeleteMessage(
            selection.length,
            BulkDeleteItemNames.Users
          )
        : ModalPopupMessages.singleDeleteMessage(
            AttachmentDownloadRequestType.user
          ) + `${selection[0].fullName}?`;

    return this.modalService.openPopup(
      {
        message: message,
        isFooterRequired: true,
        isBulk: selection.length > 1,
        isMultiple: true,
        isForCreateLinkedAccount: false,
      },
      selection.length > 1
        ? ModalPopupMessages.BulkDeleteHeader
        : ModalPopupMessages.singleDeleteHeader(
            AttachmentDownloadRequestType.user
          ),
      MessagePopupComponent,
      popupWidth.deletePopup
    );
  }

  public deleteUsers(selection: AccountUser[]) {
    const deleteAPICall = () =>
      this.baseService
        .initiateApiRequest(
          'POST',
          URLConstants.accountUsers.delete,
          { ids: selection.map(item => item.id) },
          '',
          'json'
        )
        .pipe(
          this.errorHandler({
            showRedErrorToast: error => error.status === 400,
          }),
          tap(response => {
            if (response.statusCode === StatusCodes.Success) {
              this.snakbarService.openCustomisedSnackBar(
                response.message,
                ModalPopupMessages.success
              );
            }
          })
        );

    return this.deleteUserConfirmation(selection)
      .afterClosed()
      .pipe(switchMap(data => (data ? deleteAPICall() : of(false))));
  }

  public resendEmailWithConfirmation(ids: number[]) {
    const dialogRef = this.modalService.openPopup(
      {
        isFooterRequired: true,
        message: ModalPopupMessages.accountUserResendCredentialsMessage,
        isForUserCreate: false,
        isForWarning: true,
        footerButtonList: buttonsForSendCredentials,
      },
      ModalPopupMessages.sendCredentialsMessage,
      MessagePopupComponent,
      popupWidth.downloadPopup
    );

    return dialogRef.afterClosed().pipe(
      filter(data => data),
      switchMap(() =>
        this.baseService
          .initiateApiRequest(
            'POST',
            URLConstants.accountUsers.resetPassword,
            { ids },
            '',
            'json'
          )
          .pipe(
            this.errorHandler({
              showRedErrorToast: error => error.status === 400,
            }),
            tap(response => {
              if (response.statusCode === StatusCodes.Success) {
                this.snakbarService.openCustomisedSnackBar(
                  response.message,
                  ModalPopupMessages.success
                );
              }
            })
          )
      )
    );
  }

  public addorUpdateAccountRouting(data: any) {
    return this.baseService
      .initiateApiRequest(
        'PUT',
        URLConstants.addOrUpdateAccountRouting,
        data,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public setRoutingCriteriaFromAPI(data: any[]): void {
    this.routingCriteriaFromAPI.next(data || []);
  }
 
  public getRoutingCriteriaFromAPI(): any[] {
    return this.routingCriteriaFromAPI.getValue();
  }

  public clearRoutingCriteriaFromAPI(): void {
    this.routingCriteriaFromAPI.next([]);
  }
  
  public deleteAccountRouting(data: { accountId: number; criteriaIds: number[] }): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteAccountRouting,
        data,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public generateUniqueTempId(): string {
    const timestamp = Date.now().toString(36);
    const array = new Uint32Array(1);
    globalThis.crypto.getRandomValues(array);
    return timestamp + '-' + array[0];
  }
}
